import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, unref as _unref, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "favorites-container" }
const _hoisted_2 = {
  key: 0,
  class: "loading"
}
const _hoisted_3 = {
  key: 1,
  class: "no-data"
}
const _hoisted_4 = {
  key: 2,
  class: "posts-list"
}
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "post-header" }
const _hoisted_7 = { class: "post-author-left" }
const _hoisted_8 = { class: "post-author-info" }
const _hoisted_9 = { class: "post-time" }
const _hoisted_10 = { class: "header-actions" }
const _hoisted_11 = { class: "post-content" }
const _hoisted_12 = { class: "post-title" }
const _hoisted_13 = { class: "post-text" }
const _hoisted_14 = ["src"]
const _hoisted_15 = { class: "post-stats" }
const _hoisted_16 = { class: "stat-item" }
const _hoisted_17 = { class: "stat-item" }
const _hoisted_18 = { class: "stat-item" }
const _hoisted_19 = { class: "stat-item" }
const _hoisted_20 = {
  key: 0,
  class: "post-actions"
}

import { ref, onMounted, computed } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useStore } from 'vuex';
import { getUserFavorites } from '@/user/api/userApi';
import { toggleLikePost, toggleFavoritePost, followUser, deletePost as deletePostAPI } from '@/user/api/post';
import { formatDate } from '@/utils/date';
import { 
  ThumbUp as ThumbUpIcon, 
  ChatLineRound as ChatLineRoundIcon, 
  Star as StarIcon, 
  Share as ShareIcon 
} from '@element-plus/icons-vue';
import EditPostModal from './EditPostModal.vue'; // 导入编辑帖子模态框组件

interface Post {
  id: number;
  title: string;
  content: string;
  coverImage?: string;
  viewCount: number;
  likeCount: number;
  favoriteCount: number;
  commentCount: number;
  userId: number;       // 帖子发布者的ID
  name: string;         // 帖子发布者的姓名
  avatar: string;       // 帖子发布者的头像
  isFollowed: number;   // 是否已关注发布者 (0: 未关注, 1: 已关注)
  createTime?: string;  // 创建时间
}


export default /*@__PURE__*/_defineComponent({
  __name: 'UserFavoritesView',
  emits: ['show-post-detail'],
  setup(__props) {

const store = useStore(); // 获取store实例

// 定义组件可以触发的事件


const posts = ref<Post[]>([]);
const loading = ref(true);
const currentPage = ref(1);
const total = ref(0);
const pageSize = ref(5);

// 编辑相关状态
const showEditModal = ref(false);
const currentEditPostId = ref<number | null>(null);

// 计算属性获取当前登录用户的ID
const computedUserId = computed(() => {
  const userInfo = store.state.userInfo;
  return userInfo?.id || null;
});

// 处理编辑按钮点击
const handleEditPost = (post: Post) => {
  currentEditPostId.value = post.id;
  showEditModal.value = true;
};

// 处理删除按钮点击
const handleDeletePost = async (post: Post) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除帖子 "${post.title}" 吗？此操作不可撤销。`,
      '删除确认',
      {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    );

    const response = await deletePostAPI(post.id);
    if (response.code === 1) {
      ElMessage.success('删除成功');
      // 从列表中移除该帖子
      const index = posts.value.findIndex(p => p.id === post.id);
      if (index !== -1) {
        posts.value.splice(index, 1);
        total.value--;
      }
    } else {
      ElMessage.error(response.msg || '删除失败');
    }
  } catch (error) {
    console.error('删除帖子失败:', error);
    if (error !== 'cancel') { // 如果不是用户取消操作
      ElMessage.error('删除失败，请稍后重试');
    }
  }
};

// 处理保存成功
const handleSaveSuccess = () => {
  ElMessage.success('帖子更新成功');
  loadFavorites(); // 重新加载收藏列表
};

// 处理模态框关闭
const handleModalClose = () => {
  currentEditPostId.value = null;
};

// 处理关注/取消关注
const handleToggleFollow = async (post: Post) => {
  try {
    // 如果用户未登录，提示登录
    if (!store.getters.isAuthenticated) {
      ElMessage.warning('请先登录');
      return;
    }

    // 调用API进行关注/取消关注
    const response = await followUser(post.userId);

    if (response && response.code === 1) {
      // 更新关注状态 - 反转当前状态
      post.isFollowed = !post.isFollowed;
      ElMessage.success(post.isFollowed ? '已关注' : '取消关注成功');
    } else {
      ElMessage.error(response.msg || '操作失败');
    }
  } catch (error) {
    console.error('操作失败:', error);
    ElMessage.error('操作失败，请稍后重试');
  }
};

// 获取收藏列表
const loadFavorites = async () => {
  try {
    loading.value = true;
    const params = {
      page: currentPage.value,
      pageSize: pageSize.value
    };
    
    const response = await getUserFavorites(params);
    console.log('收藏列表响应数据:', response); // 调试信息
    
    // 处理可能的数据结构差异
    let responseData;
    if (response.data) {
      // 如果响应在外层有一个data包装
      responseData = response.data;
    } else {
      // 如果响应直接就是数据
      responseData = response;
    }
    
    // 检查数据结构，兼容不同格式
    if (responseData && responseData.records) {
      // 标准格式: { records: [...], total: x }
      posts.value = responseData.records.map(post => ({
        ...post,
        isFollowed: Boolean(post.isFollowed)  // 将数字类型转换为布尔值
      }));
      total.value = responseData.total || 0;
    } else if (Array.isArray(responseData)) {
      // 直接数组格式: [...]
      posts.value = responseData.map(post => ({
        ...post,
        isFollowed: Boolean(post.isFollowed)  // 将数字类型转换为布尔值
      }));
      total.value = responseData.length;
    } else if (responseData && responseData.data && responseData.data.records) {
      // 双层包装格式: { data: { records: [...], total: x }}
      posts.value = responseData.data.records.map(post => ({
        ...post,
        isFollowed: Boolean(post.isFollowed)  // 将数字类型转换为布尔值
      })) || [];
      total.value = responseData.data.total || 0;
    } else {
      // 尝试其他可能的字段名
      const possibleRecordsKeys = ['records', 'data', 'list', 'items'];
      let foundRecords = false;
      
      for (const key of possibleRecordsKeys) {
        if (responseData[key]) {
          posts.value = Array.isArray(responseData[key]) ? responseData[key] : [];
          foundRecords = true;
          break;
        }
      }
      
      if (!foundRecords) {
        posts.value = [];
      }
      
      // 查找总数字段
      const possibleTotalKeys = ['total', 'totalCount', 'count'];
      for (const key of possibleTotalKeys) {
        if (responseData[key] !== undefined) {
          total.value = responseData[key] || 0;
          break;
        }
      }
    }
    
    console.log(`加载到 ${posts.value.length} 条收藏，总计 ${total.value}`); // 调试信息
    console.log('第一条收藏数据结构:', posts.value.length > 0 ? JSON.stringify(posts.value[0], null, 2) : '暂无数'); // 调试信息
  } catch (error) {
    console.error('获取收藏列表失败:', error);
    ElMessage.error('获取收藏列表失败');
  } finally {
    loading.value = false;
  }
};


// 分页改变
const handlePageChange = (page: number) => {
  currentPage.value = page;
  loadFavorites();
};

onMounted(() => {
  loadFavorites();
});

return (_ctx: any,_cache: any) => {
  const _component_el_avatar = _resolveComponent("el-avatar")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_pagination = _resolveComponent("el-pagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[4] || (_cache[4] = _createElementVNode("h2", { class: "page-title" }, "我的收藏", -1)),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, "加载中..."))
      : (posts.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[1] || (_cache[1] = [
            _createElementVNode("i", { class: "el-icon-star-off" }, null, -1),
            _createElementVNode("div", null, "暂无收藏内容", -1)
          ]))]))
        : (_openBlock(), _createElementBlock("div", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(posts.value, (post) => {
              return (_openBlock(), _createElementBlock("div", {
                key: post.id,
                class: "post-card",
                onClick: ($event: any) => (_ctx.$emit('show-post-detail', post.id))
              }, [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("div", _hoisted_7, [
                    _createVNode(_component_el_avatar, {
                      size: 40,
                      src: post.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
                      class: "author-avatar"
                    }, null, 8, ["src"]),
                    _createElementVNode("div", _hoisted_8, [
                      _createElementVNode("h4", null, _toDisplayString(post.name), 1),
                      _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(formatDate)(post.createTime)), 1)
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_10, [
                    (computedUserId.value === post.userId)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [], 64))
                      : (_openBlock(), _createBlock(_component_el_button, {
                          key: 1,
                          type: post.isFollowed ? 'success' : 'default',
                          size: "small",
                          class: "follow-btn",
                          onClick: _withModifiers(($event: any) => (handleToggleFollow(post)), ["stop"])
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(post.isFollowed ? '已关注' : '+ 关注'), 1)
                          ]),
                          _: 2
                        }, 1032, ["type", "onClick"]))
                  ])
                ]),
                _createElementVNode("div", _hoisted_11, [
                  _createElementVNode("h3", _hoisted_12, _toDisplayString(post.title), 1),
                  _createElementVNode("p", _hoisted_13, _toDisplayString(post.content), 1),
                  (post.coverImage)
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        src: post.coverImage,
                        alt: "cover",
                        class: "post-cover"
                      }, null, 8, _hoisted_14))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("span", _hoisted_16, [
                    _createVNode(_component_el_icon, { size: 14 }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(ThumbUpIcon))
                      ]),
                      _: 1
                    }),
                    _createTextVNode(_toDisplayString(post.likeCount), 1)
                  ]),
                  _createElementVNode("span", _hoisted_17, [
                    _createVNode(_component_el_icon, { size: 14 }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(ChatLineRoundIcon))
                      ]),
                      _: 1
                    }),
                    _createTextVNode(_toDisplayString(post.commentCount), 1)
                  ]),
                  _createElementVNode("span", _hoisted_18, [
                    _createVNode(_component_el_icon, { size: 14 }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(StarIcon))
                      ]),
                      _: 1
                    }),
                    _createTextVNode(_toDisplayString(post.favoriteCount), 1)
                  ]),
                  _createElementVNode("span", _hoisted_19, [
                    _createVNode(_component_el_icon, { size: 14 }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(ShareIcon))
                      ]),
                      _: 1
                    }),
                    _createTextVNode(_toDisplayString(post.shareCount), 1)
                  ])
                ]),
                (computedUserId.value === post.userId)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                      _createVNode(_component_el_button, {
                        size: "small",
                        type: "primary",
                        class: "action-btn edit-btn",
                        onClick: _withModifiers(($event: any) => (handleEditPost(post)), ["stop"])
                      }, {
                        default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                          _createTextVNode(" 编辑 ", -1)
                        ]))]),
                        _: 1
                      }, 8, ["onClick"]),
                      _createVNode(_component_el_button, {
                        size: "small",
                        type: "danger",
                        class: "action-btn delete-btn",
                        onClick: _withModifiers(($event: any) => (handleDeletePost(post)), ["stop"])
                      }, {
                        default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                          _createTextVNode(" 删除 ", -1)
                        ]))]),
                        _: 1
                      }, 8, ["onClick"])
                    ]))
                  : _createCommentVNode("", true)
              ], 8, _hoisted_5))
            }), 128))
          ])),
    _createVNode(EditPostModal, {
      modelValue: showEditModal.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showEditModal).value = $event)),
      "post-id": currentEditPostId.value,
      onSaveSuccess: handleSaveSuccess,
      onClose: handleModalClose
    }, null, 8, ["modelValue", "post-id"]),
    (total.value > pageSize.value)
      ? (_openBlock(), _createBlock(_component_el_pagination, {
          key: 3,
          class: "pagination",
          "current-page": currentPage.value,
          "page-size": pageSize.value,
          total: total.value,
          layout: "prev, pager, next",
          onCurrentChange: handlePageChange
        }, null, 8, ["current-page", "page-size", "total"]))
      : _createCommentVNode("", true)
  ]))
}
}

})